import {
  OmniElement,
  repeat,
  ImageAPI,
  classMap,
  html,
  css,
  nothing,
  ref,
  createRef,
} from 'omni-ui';
import { produce } from 'immer';
import { formatDate } from '../helpers/utils.js';
import { MAX_IMAGE_BYTE_SIZE } from '../helpers/constants.js';

/** callback for sorting cards so that pinned shows first, followed by modified date desc */
const cardsCompareFn = (a, b) => {
  if (a.isPinned) return -1;
  return b.isPinned ? 1 : new Date(b.last_modified) - new Date(a.last_modified);
};
export class CampaignPinnableCards extends OmniElement {
  /**
   * Attach Objects Lit Properties
   * @property {Array} cards Arrary of card objects holding context, image, updated date, and user info.
   * @property {String} tempTextValue Temporary text value to hold input text to be added to card object arrary.
   * @property {String} tempImageUrl Temporary image URL value to hold input text to be added to card object arrary.
   * @property {Boolean} isUploading State boolean to disable save/edit button while image is uploading.
   * @property {String} currentlyEditing Index value of the card object to be editted or null value to respresent a newly added card object.
   * @property {Boolean} isAdding State boolean to represent adding a new card.
   * @property {String} assistFieldId - The id of the field for assist integration
   * @property {Function} assistTemplateContext - Function to help resolve templates in assist prompt
   */
  static get properties() {
    return {
      cards: { type: Array },
      tempTextValue: { type: String, state: true },
      tempImageUrl: { type: String, state: true },
      isUploading: { type: Boolean, state: true },
      currentlyEditing: { type: String },
      isAdding: { type: Boolean },
      assistFieldId: { type: String },
      assistTemplateContext: { type: Function },
      buttonText: { type: String, state: true },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .module-container {
          position: relative;
          min-height: 35rem;
        }
        .box-title {
          display: flex;
          width: 20.357rem;
        }
        .box-title-text {
          display: flex;
          gap: 0.2rem;
          font-size: 1.071rem;
          margin-right: 2rem;
        }
        .input-container {
          margin-bottom: 2.143rem;
        }
        .input-title {
          margin-bottom: 0.714rem;
        }
        .tl-card-container {
          margin: 0 1.5rem 1.5rem 1.5rem;
          display: grid;
          grid-template-columns: repeat(
            auto-fill,
            minmax(max(20rem, calc((100% - 4rem) / 3)), 1fr)
          );
          grid-gap: 0.5rem;
          height: 25rem;
          overflow: auto;
        }
        .tl-card {
          margin: 0.714rem;
          height: 7rem;
        }
        .tl-card-top {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        }
        .tl-card-text {
          margin-bottom: 0.714rem;
          overflow: hidden;
        }
        .omni .tooltip {
          margin: 0;
        }
        .button-container {
          display: flex;
          flex: 0 1 auto;
          justify-content: flex-end;
        }
        .hide,
        .button-container .toggle-open-btn {
          visibility: hidden;
        }
        .card-content:hover .button-container button,
        .card-content:hover .button-container .toggle-open-btn {
          visibility: visible;
        }
        .card-content {
          display: flex;
          flex-direction: row;
        }
        .image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .img-placeholder {
          width: 100%;
          height: 100%;
          font-size: 3.4rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .img-placeholder omni-icon {
          --color-icon-lines: var(--color-core-medium);
          fill: var(--color-icon-lines);
        }
        .tl-card-left {
          width: 4rem;
          height: 4rem;
          display: block;
          margin-right: 1rem;
        }
        .tl-card-right {
          width: 14rem;
          flex: 1 1 auto;
        }
        .is-pinned {
          fill: rgb(var(--rgb-primary)) !important;
        }

        .omni .module-container-input {
          position: absolute;
          z-index: 5;
          display: flex;
          flex-flow: column nowrap;
          width: 25.357rem;
          height: fit-content;
          padding: 0;

          top: var(--pop-over-y);
          left: var(--pop-over-x);
        }

        .omni .open-btn {
          min-width: 3.143rem;
          height: 2.571rem;
        }

        .omni header {
          display: flex;
          width: 100%;
          height: 3rem;
          border-bottom: 0.071rem solid rgb(var(--rgb-border));
          justify-content: flex-start;
          align-items: center;
          padding: 0.714rem 1.143rem;
        }

        .omni header span.header-text {
          opacity: 0.9;
          font-size: 1rem;
          line-height: 1;
          color: rgb(var(--rgb-core-dark));
        }

        .omni main {
          display: flex;
          flex-flow: column nowrap;
          margin: 1.714rem 1.714rem 0 1.714rem;
        }

        .omni main .error {
          color: red;
          font-size: 0.857rem;
        }

        .omni footer.footer-btns {
          display: flex;
          justify-content: flex-end;
          margin: 0 1.714rem 1.714rem 1.714rem;
        }

        .omni footer button.button.is-text {
          margin-right: 0.714rem;
        }
        .tooltip-invoker {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `,
    ];
  }

  constructor() {
    super();
    this.tempTextValue = '';
    this.imageAPI = null;
    this.ANsid = window?.AN?.sso?.session?.sid;
    this.tempImageUrl = '';
    this.isUploading = false;
    this.cards = [];
    this.currentlyEditing = null;
    this.isAdding = false;
    this.containerRef = createRef();
    this.inputCardRef = createRef();
    this.cardHidden = true;
  }

  connectedCallback() {
    super.connectedCallback();
    this.imageAPI = new ImageAPI(this.ANsid, this.env);
    document.addEventListener('click', this.#closeInputCard);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#closeInputCard);
    super.disconnectedCallback();
  }

  /** menthod that handles passing the data up to the parent component to be saved. */
  #handleChildChanges = () => {
    this.dispatchNewEvent('card-change', {
      detail: {
        cards: this.cards,
      },
    });
  };

  /**
   * @param {*} event passed by the event listener "click"
   * @description mentod that handles the closing of the component
   * @returns out of the meth
   */
  #closeInputCard = async event => {
    const { value: inputCard } = this.inputCardRef;
    const inputCardButton = [
      ...this.shadowRoot.querySelectorAll('[data-input-card-button]'),
    ];
    if (
      (this.currentlyEditing === null && !this.isAdding) ||
      event.composedPath().includes(inputCard) ||
      event.composedPath().some(element => inputCardButton.includes(element))
    ) {
      return;
    }
    this.currentlyEditing = null;
    this.isAdding = false;
  };

  /** reset the temporary card leader name and image URL state properties to empty strings */
  #resetTempTextValues = () => {
    this.tempTextValue = '';
    this.tempImageUrl = '';
  };

  /** method to set card object and push to card arrary. Calls #handleChildChanges to save data */
  #addItem = () => {
    const toAdd = {
      text: this.tempTextValue,
      userName: window.AN?.sso?.user?.fullName ?? '',
      last_modified: new Date().toISOString(),
      img: this.tempImageUrl,
      isPinned: false,
    };

    this.cards = produce(this.cards, draft => {
      draft.push(toAdd);
      draft.sort(cardsCompareFn);
    });

    this.#handleChildChanges();
    this.#resetTempTextValues();
    this.#resetCurrentlyEditing();
    this.isAdding = false;
  };

  /**
   * @param {*} number index of the individual card to be edited
   * @description method to edit a single card object
   * Calls #handleChildChanges to save data
   */
  #editItem = idx => {
    this.cards = produce(this.cards, draft => {
      draft[idx].text = this.tempTextValue || draft[idx].text;
      draft[idx].last_modified = new Date().toISOString();

      if (this.tempImageUrl === undefined) {
        draft[idx].img = '';
      } else {
        draft[idx].img = this.tempImageUrl || draft[idx].img;
      }

      draft.sort(cardsCompareFn);
    });

    this.#handleChildChanges();
    this.#resetTempTextValues();
    this.#resetCurrentlyEditing();
  };

  /**
   * @param {*} number index of the individual card to be deleted
   * @description method to delete a single card object
   * Calls #handleChildChanges to save data
   */
  #deleteItem = idx => {
    this.cards = produce(this.cards, draft => {
      draft.splice(idx, 1);
    });

    this.#handleChildChanges();
    this.#resetCurrentlyEditing();
  };

  #togglePin = idx => {
    this.cards = produce(this.cards, draft => {
      draft.forEach((card, i) => {
        // eslint-disable-next-line no-param-reassign
        card.isPinned = i === idx ? !card.isPinned : false;
      });

      draft.sort(cardsCompareFn);
    });

    this.#handleChildChanges();
    this.#resetTempTextValues();
  };

  #resetCurrentlyEditing = () => {
    this.currentlyEditing = null;
  };

  #handleSetEditing = (e, idx) => {
    if (this.currentlyEditing === idx) {
      this.#resetCurrentlyEditing();
      return;
    }
    const card = this.cards[idx];

    this.#setInputCardPosition(e);

    this.tempTextValue = card?.text?.trim() || '';
    this.tempImageUrl = card?.img?.trim() || '';

    this.currentlyEditing = idx;
  };

  /**
   * @param {*} event
   * @description method to set the input card pop up position relative to button.
   * Detects edge of module to change render position.
   */
  #setInputCardPosition = ({ target }) => {
    const button = target.closest('button');
    const { value: container } = this.containerRef;

    const {
      x: buttonLeft,
      y: buttonTop,
      height: buttonHeight,
      width: buttonWidth,
    } = button.getBoundingClientRect();

    const {
      x: containerLeft,
      y: containerTop,
      height: containerHeight,
      width: containerWidth,
    } = container.getBoundingClientRect();

    if (buttonLeft + 355 > containerLeft + containerWidth) {
      this.style.setProperty(
        '--pop-over-x',
        `${buttonLeft + buttonWidth - 355 - containerLeft}px`,
      );
    } else {
      this.style.setProperty('--pop-over-x', `${buttonLeft - containerLeft}px`);
    }

    if (buttonTop + 300 + 82 > containerTop + containerHeight) {
      this.style.setProperty(
        '--pop-over-y',
        `${buttonTop - 300 - 10 - containerTop}px`,
      );
    } else {
      this.style.setProperty(
        '--pop-over-y',
        `${buttonTop + buttonHeight - containerTop + 10}px`,
      );
    }
  };

  #renderInputCard = () => {
    if (this.currentlyEditing === null && !this.isAdding) {
      return nothing;
    }

    const idx = this.currentlyEditing;
    const card = this.cards[idx];

    const hasCard = this.tempTextValue?.trim();
    const cardChanged = this.tempTextValue?.trim() !== card?.text?.trim();
    const imageChanged = this.tempImageUrl?.trim() !== card?.img?.trim();

    const canSubmit =
      this.currentlyEditing !== null
        ? imageChanged || (hasCard && cardChanged)
        : hasCard;

    this.buttonText = this.currentlyEditing !== null ? 'Edit' : 'Add';
    const headerText = `${this.buttonText} ${this.schema?.cardHeaderText}`;

    return html`
      <div class="box module-container-input " ${ref(this.inputCardRef)}>
        <header>
          <span class="header-text">${headerText}</span>
        </header>
        <main class="card-inputs">
          ${this.error
            ? html`<div class="error mb-5">${this.error}</div>`
            : nothing}
          <div class="input-container">
            <div class="input-title is-size-6">
              ${this.schema?.cardHeaderText}
            </div>
            <!-- TODO change fieldID to more generic name -->
            <campaign-assist-field
              fieldId=${this.assistFieldId}
              .fields=${this.context.assistFields}
              .templateContext=${this.assistTemplateContext}
            >
              <textarea
                class="input input-card-input"
                placeholder=${`Describe the ${this.schema?.cardHeaderText}`}
                .value=${card ? card.text : this.tempTextValue}
                @input=${e => {
                  this.tempTextValue = e.target.value;
                }}
                @keydown=${e => {
                  if (e.key === 'Enter' && canSubmit) {
                    if (card) {
                      this.#editItem(idx);
                    } else {
                      this.#addItem();
                    }
                  }
                }}
              ></textarea>
            </campaign-assist-field>
          </div>
          <div class="input-container">
            <om2-image-upload
              env=${this.env}
              ANsid=${this.ANsid}
              value=${card?.img}
              maxSize=${MAX_IMAGE_BYTE_SIZE}
              @change=${e => {
                this.tempImageUrl = e.detail.value;
              }}
            >
            </om2-image-upload>
          </div>
        </main>
        <footer class="footer-btns">
          <button
            class="button is-text"
            @click=${() => {
              this.#resetTempTextValues();
              this.isAdding = false;
              this.currentlyEditing = null;
            }}
          >
            Cancel
          </button>
          <button
            ?disabled=${this.isUploading || !canSubmit}
            @click=${async () => {
              if (card) {
                this.#editItem(idx);
              } else {
                this.#addItem();
              }
              await this.updateComplete;
            }}
            class=${classMap({
              button: true,
              'is-outlined': true,
              'is-loading': this.isUploading,
            })}
          >
            ${this.buttonText}
          </button>
        </footer>
      </div>
    `;
  };

  #renderCard = idx => {
    const card = this.cards[idx];
    const subtitle = `${card.userName} ${formatDate(card.last_modified, true)}`;

    return html`
      <div class="tl-card card">
        <div class="card-content">
          <div class="tl-card-left">
            ${card.img
              ? html`
                  <img class="image" loading="lazy" src=${card.img} alt="" />
                `
              : html`
                  <div class="img-placeholder">
                    <omni-icon icon-id="omni:app:contentAutomation"></omni-icon>
                  </div>
                `}
          </div>
          <div class="tl-card-right">
            <div class="tl-card-top">
              <omni-tooltip class="tl-card-text">
                <div slot="invoker" class="tooltip-invoker is-5">
                  ${card.text || 'Missing text'}
                </div>
                <div slot="content">${card.text || 'Missing text'}</div>
              </omni-tooltip>
              <div class="button-container">
                <!-- Render Edit Button -->
                <div>
                  <omni-tooltip
                    class="invoker-tooltip ${this.readonly ? 'is-hidden' : ''}"
                  >
                    <div slot="content">Edit</div>
                    <button
                      class="button is-text is-small hide tl-button"
                      id="viz4-btn"
                      slot="invoker"
                      @click=${e => {
                        this.#handleSetEditing(e, idx);
                      }}
                      data-input-card-button
                    >
                      <omni-icon
                        class="is-size-4"
                        icon-id="omni:interactive:edit"
                      ></omni-icon>
                    </button>
                  </omni-tooltip>
                </div>
                <!-- Render Delete Button  -->
                <omni-tooltip
                  class="invoker-tooltip ${this.readonly ? 'is-hidden' : ''}"
                >
                  <div slot="content">Delete</div>
                  <button
                    slot="invoker"
                    class="button is-text is-small hide tl-button"
                    @click=${() => {
                      this.#deleteItem(idx);
                    }}
                  >
                    <omni-icon
                      class="is-size-4"
                      icon-id="omni:interactive:delete"
                    ></omni-icon>
                  </button>
                </omni-tooltip>
                <!-- Render Pin Button  -->
                <omni-tooltip
                  class="invoker-tooltip ${this.readonly ? 'is-hidden' : ''}"
                >
                  <div slot="content">Pin</div>
                  <button
                    slot="invoker"
                    class=${classMap({
                      hide: !card?.isPinned,
                      button: true,
                      'is-text': true,
                      'is-small': true,
                    })}
                    @click=${() => {
                      this.#togglePin(idx);
                    }}
                  >
                    <omni-icon
                      class=${classMap({
                        'is-pinned': card?.isPinned,
                        'is-size-4': true,
                      })}
                      icon-id="omni:interactive:pin"
                    ></omni-icon>
                  </button>
                </omni-tooltip>
              </div>
            </div>
            <p class="subtitle">${subtitle || 'Missing User Info'}</p>
          </div>
        </div>
      </div>
    `;
  };

  render() {
    return html`
      <omni-style>
        <div class="box module-container" ${ref(this.containerRef)}>
          <div class="box-title">
            <div class="box-title-text">
              ${this.schema?.pt2_module_title}
              <omni-tooltip>
                <omni-icon
                  slot="invoker"
                  class="is-size-2"
                  icon-id="omni:informative:info"
                ></omni-icon>
                <div slot="content">${this.schema?.pt2_tooltip_text}</div>
              </omni-tooltip>
            </div>
            <!-- Render Add Card Button -->
            <div>
              <omni-tooltip
                class="invoker-tooltip ${this.readonly ? 'is-hidden' : ''}"
              >
                <div slot="content">Add ${this.schema?.cardHeaderText}</div>
                <button
                  class="button is-small is-outlined"
                  id="viz4-btn"
                  slot="invoker"
                  @click=${async e => {
                    this.#resetCurrentlyEditing();
                    this.#resetTempTextValues();
                    await this.updateComplete;
                    this.#setInputCardPosition(e);
                    this.isAdding = true;
                  }}
                  data-input-card-button
                >
                  <span>Add ${this.schema?.cardHeaderText}</span>
                </button>
              </omni-tooltip>
            </div>
          </div>
          <!-- Render Card container & cards -->
          <div class="tl-card-container">
            ${repeat(
              this.cards,
              card => `${card.last_modified}-${card.userName}`,
              (_, idx) => this.#renderCard(idx),
            )}
          </div>
          ${this.#renderInputCard()}
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-pinnable-cards', CampaignPinnableCards);
